import { formatJson, formatXml } from '../textEditor/formatEditorContent';

/** Format code content for supported browser-parser / JSON languages. Throws on invalid input. */
export function formatCodeContent(languageId: string, text: string): string {
  switch (languageId) {
    case 'json':
      return formatJson(text);
    case 'xml':
      return formatXml(text);
    case 'yaml':
      return formatYaml(text);
    case 'sql':
      return formatSql(text);
    default:
      throw new Error(`No browser formatter for language "${languageId}".`);
  }
}

/**
 * Indent-safe YAML normalization: expand leading tabs, trim trailing spaces,
 * collapse excessive blank lines, ensure a trailing newline. Does not restructure
 * mappings; throws when tabs appear mid-line (unsafe to auto-fix).
 */
export function formatYaml(text: string): string {
  if (!text.trim()) return text.endsWith('\n') ? text : `${text}\n`;

  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    // Leading tabs are expanded; tabs mixed into content are unsafe to auto-format.
    if (/[^\t\s].*\t|\t.*[^\t\s]/.test(raw.replace(/^\t+/, ''))) {
      throw new Error(`Invalid YAML for safe format (mixed tabs) at line ${i + 1}.`);
    }
    const expanded = raw.replace(/^\t+/g, (tabs) => '  '.repeat(tabs.length));
    out.push(expanded.replace(/[ \t]+$/g, ''));
  }

  while (out.length > 0 && out[out.length - 1] === '') {
    out.pop();
  }

  const collapsed: string[] = [];
  let blankRun = 0;
  for (const line of out) {
    if (line === '') {
      blankRun += 1;
      if (blankRun <= 1) collapsed.push('');
      continue;
    }
    blankRun = 0;
    collapsed.push(line);
  }

  return `${collapsed.join('\n')}\n`;
}

/** Light SQL whitespace normalization (does not parse SQL). */
export function formatSql(text: string): string {
  const normalized = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/,\s*/g, ', ')
    .replace(/[ \t]+;/g, ';')
    .replace(/;\s*(?=\S)/g, ';\n')
    .trimEnd();

  return normalized ? `${normalized}\n` : '\n';
}
